import { Controller } from "@hotwired/stimulus";
import { Vec2 } from "./vec2";

/** Fires drag events based on pointer events inside the drag handle element */
export default class DragHandleController extends Controller<HTMLElement> {

    private previousDrag: Vec2;
    
    public connect() {
        this.element.style.cursor = 'grab';
        this.element.addEventListener('mousedown', this.onMouseDown);
        this.element.addEventListener('touchstart', this.onTouchStart, {passive: false});
        // We switch to the document in case the mouse leaves the element while dragging
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('touchmove', this.onTouchMove, {passive: false});
        document.addEventListener('mouseup', this.onPointerUp);
        document.addEventListener('mouseleave', this.onPointerUp);
        document.addEventListener('touchend', this.onPointerUp);
        document.addEventListener('touchcancel', this.onPointerUp);
    }

    public disconnect() {
        this.element.style.removeProperty('cursor');
        this.element.removeEventListener('mousedown', this.onMouseDown);
        this.element.removeEventListener('touchstart', this.onTouchStart);
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('touchmove', this.onTouchMove);
        document.removeEventListener('mouseup', this.onPointerUp);
        document.removeEventListener('mouseleave', this.onPointerUp);
        document.removeEventListener('touchend', this.onPointerUp);
        document.removeEventListener('touchcancel', this.onPointerUp);
    }

    private onPointerDown(event: Event, pointer: Vec2) {
        // The handle might contain other elements, such as buttons, which should not trigger dragging.
        // To override this behavior, use the `pointer-events: none;` style on the child element.
        if (event.target === this.element) {
            event.preventDefault();
            this.previousDrag = pointer;
        }
    }

    private onPointerMove = (event: Event, pointer: Vec2) => {
        if (this.previousDrag) {
            event.preventDefault();
            let delta = pointer.subtract(this.previousDrag);
            this.element.dispatchEvent(new CustomEvent('drag', { detail: { pointer, delta } }));
            this.previousDrag = pointer;
        }
    }

    private onPointerUp = (event: Event) => {
        if (this.previousDrag) {
            event.preventDefault();
            this.previousDrag = null;
        }
    }

    private onMouseDown = (event: PointerEvent) => {
        let position = this.convertPointerEvent(event);
        if (position) {
            this.onPointerDown(event, position);
        }
    }

    private onTouchStart = (event: TouchEvent) => {
        this.onPointerDown(event, this.convertTouchEvent(event));
    }

    private onMouseMove = (event: PointerEvent) => {
        let position = this.convertPointerEvent(event);
        if (position) {
            this.onPointerMove(event, position);
        }
    }

    private onTouchMove = (event: TouchEvent) => {
        this.onPointerMove(event, this.convertTouchEvent(event));
    }

    private convertPointerEvent(event: PointerEvent): Vec2|null {
        if (event.button === 2) {
            return null; // Ignore right clicks
        }
        return new Vec2(event.clientX, event.clientY);
    }

    private convertTouchEvent(event: TouchEvent): Vec2 {
        let { clientX, clientY } = event.touches[0];
        return new Vec2(clientX, clientY);
    }
}
